import { useContext, useState } from "react";
import { AuthContext } from "../context/authContext";
import axios from "axios";
import { baseUrl } from "../modules/account/login";
import { useNavigate } from "react-router";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

export function useUserPhones() {
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const navigate = useNavigate();

  const getData = () => {
    return axios
      .get(`${baseUrl}/profile/get_user_phones`, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        const data = response.data;
        return data;
      })
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired")
          navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["user_phones", token],
    queryFn: () => getData(),
    placeholderData: keepPreviousData,
  });

  return { data, error, isLoading, refetch };
}

export const useBuyPhone = (country_code, phone_type) => {
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const navigate = useNavigate();

  const buyPhone = async () => {
    try {
      const response = await axios.post(
        `${baseUrl}/profile/settings/phone_numbers/buy`,
        { country_code, phone_type, channel: "Twilio" },
        {
          headers: { Authorization: AuthStr },
        }
      );
      return response.data;
    } catch (e) {
      if (e?.response?.data?.message === "Session expired") {
        navigate("/account/logout");
      }
      throw new Error(
        e.response?.data?.details?.message ?? "Check your internet connection"
      );
    }
  };

  const mutation = useMutation({
    mutationFn: buyPhone,
    retry: false,
  });

  return mutation;
};

export function useDeletePhone({ id }: { id: string }) {
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const navigate = useNavigate();

  const buyPhone = () => {
    return axios
      .delete(`${baseUrl}/profile/settings/phone_numbers/${id}`, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        const data = response.data;
        return data;
      })
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired")
          navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: [`delete_phone_${id}`, token],
    queryFn: () => buyPhone(),
    placeholderData: keepPreviousData,
    enabled: false,
    retry: false,
  });

  return { data, error, isLoading, refetch };
}

export function useUpdateProfile() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (details: {
      firstname: string;
      lastname: string;
      business_name: string;
      business_about: string;
      business_country: string;
      email: string;
    }) => {
      try {
        const response = await axios.put(
          `${baseUrl}/profile/update_profile`,
          details,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        return response.data;
      } catch (e: any) {
        if (e?.response?.data?.message === "Session expired") {
          navigate("/account/logout");
        }
        throw new Error(e?.message ?? "Check your internet connection");
      }
    },
  });
}
